import { createColumnHelper } from "@tanstack/react-table";
import { useOrderInvoicing } from "../context/order-invoicing-state";
import { useRoles } from "src/context/roles-context";
import { OrderDispatches } from "../components/order-dispatches";
import { OrderNotes } from "../components/order-notes";
import { BillingComments } from "../components/billing-comments";
import { OrderDocuments } from "../components/order-documents";
import { ImageViewer } from "../components/image-viewer";
import { OrderAddNoteForm } from "../components/order-add-note-form";
import { OrderUploadDocumentForm } from "../components/order-upload-document";
import { OrderQueueManagerForm } from "../components/order-queue-manager-form";
import dayjs from "dayjs";
import { OrderInvoicingTable } from "../components/order-invoicing-table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { unbilledOrdersQueryOptions } from "src/api/mvte-query-options";
import { MdCancel, MdDoNotDisturbOn } from "react-icons/md";
import { FaCheckCircle } from "react-icons/fa";
import { ExpandableComponent } from "../components/expandable-component";
import { views } from "../data/views";
import { UnbilledOrder } from "src/types/unbilled-order";
import { QueryClient } from "@tanstack/react-query";
import { CellContext } from "@tanstack/react-table";
import { orderImagesQueryOptions } from "src/api/mvte-query-options";
import { useEffect } from "react";

const unBilldedOrdercolumnHelper = createColumnHelper<UnbilledOrder>();

export function renderLumperStatusCell(
    info: CellContext<UnbilledOrder, number | null | undefined>,
    queryClient: QueryClient,
) {
    const fee = info.getValue();
    const row = info.row.original;
    const hasLumperFee = fee !== null && fee !== undefined && fee > 0;

    if (hasLumperFee) {
        const cleanOrderNumber = (row.orderNumber || "").trim();

        const queryKey = orderImagesQueryOptions({
            orderNumber: cleanOrderNumber,
        }).queryKey;

        const cachedDocuments = queryClient.getQueryData<any[]>(queryKey);

        if (cachedDocuments === undefined) {
            return (
                <span className="flex justify-center text-lg text-yellow-300">
                    <MdDoNotDisturbOn />
                </span>
            );
        }
        const lmpDocs = cachedDocuments.filter((doc) => doc.docType === "LMP");

        const totalLmpPages = lmpDocs.reduce((acc, curr) => {
            return acc + (curr.imageUrl !== null ? 1 : 0);
        }, 0);

        const isLmpMissingPages = lmpDocs.length === 0 || totalLmpPages === 0;

        if (isLmpMissingPages) {
            return (
                <span className="flex justify-center text-lg">
                    <MdDoNotDisturbOn className="text-yellow-500" />
                </span>
            );
        }

        return (
            <span className="flex justify-center text-lg">
                <FaCheckCircle className="text-green-500" />
            </span>
        );
    }

    return (
        <span className="flex justify-center text-lg">
            <MdCancel className="text-red-500" />
        </span>
    );
}

export function IndexPage() {
    //Authentication
    const roles = useRoles();
    const accessOI = roles.checkHasRole("OrderInvoicing.Write");
    const orderInvoicing = useOrderInvoicing();
    const queryClient = useQueryClient();
    const unbilledOrdersQuery = useQuery(
        unbilledOrdersQueryOptions({
            queueName: orderInvoicing.view.queueName,
            orderStatuses: orderInvoicing.view.orderStatuses,
        }),
    );

    useEffect(() => {
        if (unbilledOrdersQuery.data) {
            unbilledOrdersQuery.data.forEach((row) => {
                const hasLumperFee =
                    row.lumperFee !== null &&
                    row.lumperFee !== undefined &&
                    row.lumperFee > 0;

                if (hasLumperFee) {
                    const cleanOrderNumber = (row.orderNumber || "").trim();

                    queryClient.prefetchQuery(
                        orderImagesQueryOptions({
                            orderNumber: cleanOrderNumber,
                        }),
                    );
                }
            });
        }
    }, [unbilledOrdersQuery.data, queryClient]);

    const unbilledOrdersColumns = [
        unBilldedOrdercolumnHelper.accessor("orderNumber", {
            header: "Order #",
            size: 100,
        }),
        unBilldedOrdercolumnHelper.accessor("billerCode", {
            header: "Biller Code",
            cell: (info) => info.getValue(),
            size: 95,
        }),
        unBilldedOrdercolumnHelper.accessor("orderStatus", {
            header: "Status",
            cell: (info) => info.getValue(),
            meta: {
                filterVariant: "select",
            },
            size: 55,
        }),
        unBilldedOrdercolumnHelper.accessor("docRequirementMet", {
            header: "Doc Req",
            cell: (info) => {
                return (
                    <span className="flex justify-center text-lg">
                        {info.getValue() ? (
                            <FaCheckCircle className="text-green-500" />
                        ) : (
                            <MdCancel className="text-red-500" />
                        )}
                    </span>
                );
            },
            enableColumnFilter: false,
            size: 70,
        }),
        unBilldedOrdercolumnHelper.accessor("billToCode", {
            header: "BTC",
            cell: (info) => info.getValue(),
            size: 95,
        }),
        unBilldedOrdercolumnHelper.accessor("emptyCallDate", {
            header: "Empty Date",
            cell: (info) => {
                if (info.getValue() === null) {
                    return "N/A";
                }

                return dayjs(info.getValue()).format("MM/D/YYYY");
            },
            enableColumnFilter: true,
            size: 80,
        }),
        unBilldedOrdercolumnHelper.accessor("lumperFee", {
            id: "isLumper",
            header: "IsLumper",
            cell: (info) => renderLumperStatusCell(info, queryClient),
            enableColumnFilter: false,
            size: 80,
        }),
        unBilldedOrdercolumnHelper.accessor("isAccurateBillTo", {
            header: "Accurate BTC",
            cell: (info) => {
                return (
                    <span className="flex justify-center text-lg">
                        {info.getValue() ? (
                            <FaCheckCircle className="text-green-500" />
                        ) : (
                            <MdCancel className="text-red-500" />
                        )}
                    </span>
                );
            },
            enableColumnFilter: false,
            size: 90,
        }),
        unBilldedOrdercolumnHelper.accessor("hasRateID", {
            header: "Has RateID",
            cell: (info) => {
                return (
                    <span className="flex justify-center text-lg">
                        {info.getValue() ? (
                            <FaCheckCircle className="text-green-500" />
                        ) : (
                            <MdCancel className="text-red-500" />
                        )}
                    </span>
                );
            },
            enableColumnFilter: false,
            size: 90,
        }),
        unBilldedOrdercolumnHelper.accessor("hasRCS", {
            header: "Has RCS",
            cell: (info) => {
                return (
                    <span className="flex justify-center text-lg">
                        {info.getValue() ? (
                            <FaCheckCircle className="text-green-500" />
                        ) : (
                            <MdCancel className="text-red-500" />
                        )}
                    </span>
                );
            },
            enableColumnFilter: false,
            size: 60,
        }),
        unBilldedOrdercolumnHelper.accessor("hasSBL", {
            header: "Has SBL",
            cell: (info) => {
                return (
                    <span className="flex justify-center text-lg">
                        {info.getValue() ? (
                            <FaCheckCircle className="text-green-500" />
                        ) : (
                            <MdCancel className="text-red-500" />
                        )}
                    </span>
                );
            },
            enableColumnFilter: false,
            size: 60,
        }),
        unBilldedOrdercolumnHelper.accessor("isEdiBillingCustomer", {
            header: "Is EDI Cust",
            cell: (info) => {
                return (
                    <span className="flex justify-center text-lg">
                        {info.getValue() ? (
                            <FaCheckCircle className="text-green-500" />
                        ) : (
                            <MdCancel className="text-red-500" />
                        )}
                    </span>
                );
            },
            enableColumnFilter: false,
            size: 90,
        }),
        unBilldedOrdercolumnHelper.accessor("csrCode", {
            header: "CSR Code",
            size: 70,
        }),
        unBilldedOrdercolumnHelper.accessor(
            (row) => {
                const emptyDate = row.emptyCallDate;

                if (emptyDate === null) {
                    return null;
                }
                const emptyDateDayjs = dayjs(emptyDate);
                const currentDayjs = dayjs();

                return Math.abs(emptyDateDayjs.diff(currentDayjs, "day"));
            },
            {
                id: "daysEmpty",
                header: "Days Empty",
                enableColumnFilter: false,
                cell: (info) => {
                    const value = info.getValue();
                    return value === null ? "N/A" : value;
                },
                size: 80,
            },
        ),
        unBilldedOrdercolumnHelper.accessor("emptyFleet", {
            header: "Fleet",
            cell: (info) => {
                const emptyFleet = info.getValue();
                if (emptyFleet === "" || emptyFleet === null) {
                    return "N/A";
                }

                return emptyFleet;
            },
            size: 65,
        }),
        unBilldedOrdercolumnHelper.accessor("emptyDriver", {
            header: "Driver",
            cell: (info) => {
                const emptyDriver = info.getValue();

                if (emptyDriver === null || emptyDriver === "") {
                    return "N/A";
                }

                return emptyDriver;
            },
            size: 65,
        }),
        unBilldedOrdercolumnHelper.accessor("docStatus", {
            header: "Doc Status",
            enableColumnFilter: false,
            size: 75,
        }),
        unBilldedOrdercolumnHelper.accessor("teamName", {
            header: "Team Name",
            enableColumnFilter: true,
            size: 100,
        }),
    ];

    return (
        <>
            {/* Modals */}
            <dialog id="order-add-note" className="modal">
                <div className="modal-box">
                    <form method="dialog">
                        <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">
                            ✕
                        </button>
                    </form>
                    <h3 className="font-bold text-lg">Add Order Note</h3>
                    <OrderAddNoteForm
                        orderNumber={
                            orderInvoicing.selectedOrder?.orderNumber ?? ""
                        }
                        customerCode={
                            orderInvoicing.selectedOrder?.billToCode ?? ""
                        }
                        onSuccess={() =>
                            (
                                document.getElementById(
                                    "order-add-note",
                                ) as HTMLDialogElement
                            ).close()
                        }
                    />
                </div>
            </dialog>
            <dialog id="order-upload-document" className="modal">
                <div className="modal-box">
                    <form method="dialog">
                        <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">
                            ✕
                        </button>
                    </form>
                    <h3 className="font-bold text-lg">Upload Document</h3>
                    <OrderUploadDocumentForm
                        orderNumber={
                            orderInvoicing.selectedOrder?.orderNumber ?? ""
                        }
                        onSuccess={() =>
                            (
                                document.getElementById(
                                    "order-upload-document",
                                ) as HTMLDialogElement
                            ).close()
                        }
                    />
                </div>
            </dialog>
            <dialog id="order-queue-manager" className="modal">
                <div className="modal-box">
                    <form method="dialog">
                        <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">
                            ✕
                        </button>
                    </form>
                    <h3 className="font-bold text-lg">Queue Manager</h3>
                    <OrderQueueManagerForm
                        orderNumber={
                            orderInvoicing.selectedOrder?.orderNumber ?? ""
                        }
                        billToCode={
                            orderInvoicing.selectedOrder?.billToCode ?? ""
                        }
                        queueName={
                            orderInvoicing.selectedOrder?.queueName ?? ""
                        }
                        mode={
                            orderInvoicing.selectedOrder?.queueName ===
                            "Billing Hold"
                                ? "complete"
                                : orderInvoicing.selectedOrder?.queueName ===
                                    "Billing Complete"
                                  ? "add"
                                  : orderInvoicing.selectedOrder?.queueName ===
                                      "Billing"
                                    ? "add"
                                    : undefined
                        }
                        onSuccess={() =>
                            (
                                document.getElementById(
                                    "order-queue-manager",
                                ) as HTMLDialogElement
                            ).close()
                        }
                    />
                </div>
            </dialog>

            {accessOI ? (
                <div className="p-8 h-screen">
                    <div
                        className="grid h-full gap-6"
                        style={{
                            gridTemplateAreas: `
                                "views  button    docs"
                                "orders notes     docs"
                                "orders notes     docs"
                                "orders dispatch  images"
                                "orders dispatch  images"
                                "orders billing   images"
                                "orders billing   images"
                            `,
                            gridTemplateColumns:
                                "minmax(0, 4fr) minmax(0, 5fr) minmax(0, 3fr)",
                            gridTemplateRows: "auto repeat(6, minmax(0, 1fr))",
                        }}
                    >
                        <div
                            style={{ gridArea: "views" }}
                            className="space-x-4"
                        >
                            <select
                                className="select select-bordered w-full select-sm"
                                onChange={(e) =>
                                    orderInvoicing.handleSetView(e.target.value)
                                }
                                value={orderInvoicing.view.viewName}
                            >
                                <option value="">Select view</option>
                                {views.map((view) => {
                                    return (
                                        <option value={view.viewName}>
                                            {view.viewName}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        <ExpandableComponent
                            className="overflow-hidden"
                            style={{ gridArea: "orders" }}
                        >
                            <OrderInvoicingTable
                                title="Unbilled Orders"
                                data={unbilledOrdersQuery.data ?? []}
                                columns={unbilledOrdersColumns}
                                configColumns={
                                    orderInvoicing.view.ordersTable.columns
                                }
                                containerClassName="unbilled-orders"
                                pageSize={20}
                                enableRowSelection
                                onRowSelection={(rowIdxMap) => {
                                    const [rowIdx] = Object.keys(rowIdxMap);

                                    // due to our table row selection implementation this function is called on render with empty object
                                    if (rowIdx === undefined) {
                                        orderInvoicing.clearSelectedOrder();
                                        return;
                                    }

                                    orderInvoicing.setSelectedOrder(
                                        unbilledOrdersQuery.data![
                                            Number(rowIdx)
                                        ],
                                    );
                                }}
                                tableSize="xs"
                            />
                        </ExpandableComponent>

                        <div
                            style={{ gridArea: "button" }}
                            className="space-x-4"
                        >
                            <button
                                className="btn btn-sm btn-primary disabled:text-gray-500 disabled:cursor-pointer"
                                disabled={
                                    !orderInvoicing.selectedOrder?.orderNumber
                                }
                                onClick={() =>
                                    (
                                        document.getElementById(
                                            "order-add-note",
                                        ) as HTMLDialogElement
                                    ).showModal()
                                }
                            >
                                Add Note
                            </button>
                            <button
                                className="btn btn-sm btn-primary disabled:text-gray-500 disabled:cursor-pointer"
                                disabled={
                                    !orderInvoicing.selectedOrder?.orderNumber
                                }
                                onClick={() =>
                                    (
                                        document.getElementById(
                                            "order-upload-document",
                                        ) as HTMLDialogElement
                                    ).showModal()
                                }
                            >
                                Upload Document
                            </button>
                            <button
                                className="btn btn-sm btn-primary disabled:text-gray-500 disabled:cursor-pointer"
                                disabled={orderInvoicing.selectedOrder === null}
                                onClick={() =>
                                    (
                                        document.getElementById(
                                            "order-queue-manager",
                                        ) as HTMLDialogElement
                                    ).showModal()
                                }
                            >
                                Queue Manager
                            </button>
                        </div>

                        <ExpandableComponent style={{ gridArea: "notes" }}>
                            <OrderNotes
                                orderNumber={
                                    orderInvoicing.selectedOrder?.orderNumber ??
                                    null
                                }
                            />
                        </ExpandableComponent>

                        <div style={{ gridArea: "dispatch" }}>
                            <OrderDispatches
                                orderNumber={
                                    orderInvoicing.selectedOrder?.orderNumber ??
                                    null
                                }
                            />
                        </div>

                        <div style={{ gridArea: "billing" }}>
                            <BillingComments
                                orderNumber={
                                    orderInvoicing.selectedOrder?.orderNumber ??
                                    null
                                }
                                customerCode={
                                    orderInvoicing.selectedOrder?.billToCode ??
                                    null
                                }
                            />
                        </div>

                        <div style={{ gridArea: "docs" }}>
                            <OrderDocuments
                                orderNumber={
                                    orderInvoicing.selectedOrder?.orderNumber ??
                                    null
                                }
                            />
                        </div>

                        <ExpandableComponent style={{ gridArea: "images" }}>
                            <ImageViewer
                                images={orderInvoicing.selectedImages ?? null}
                            />
                        </ExpandableComponent>
                    </div>
                </div>
            ) : (
                <div className="p-4 label-text text-white">
                    Permission denied, you shouldn't be here...
                </div>
            )}
        </>
    );
}
